import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import CreateIcon from '@mui/icons-material/Create'
import Slide from '@mui/material/Slide'
import { get, post, put } from '../helpers/request'
import { useDispatch, useSelector } from 'react-redux'
import { Avatar } from '@mui/material'
import styled from 'styled-components'
import { isExtImage, isExtVideo, toBase64 } from '../utils'
import AssetConfig from './AssetConfig'
import LoadingAssets from './LoadingAssets'
import AutocompleteChip from '../components/AutocompleteChip'

const API_URL = process.env.REACT_APP_API_URL
const ASSETS_URL = `${API_URL}/v2/session/sites-manager/assets`
const TAGS_URL = `${ASSETS_URL}/tags`
const TYPES_URL = `${ASSETS_URL}/types`
const CREATE_URL = `${ASSETS_URL}/create`

const BodyContainer = styled.div`
  padding: 20px;
  height: calc(100vh - 116.5px); // Header and Footer Heights
  overflow: auto;
`

const ImageList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  grid-gap: 1em;
`

const StyledImageCard = styled.div`
  position: relative;
  width: 100%;
  height: 160px;
  cursor: pointer;
  border: 4px solid white;
  padding: 0;
  background-image: linear-gradient(45deg,#80808036 25%,transparent 25%),linear-gradient(-45deg,#80808036 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#80808036 75%),linear-gradient(-45deg,transparent 75%,#80808036 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  div, button {
    border-radius: 4px;
  }
  ${p => p.isSelected && `
    border: 4px solid #2196f3;
  `}
`

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 16px;
`

const imagePreviewStyle = { width: '100%', height: '100%' }
const titleStyle = { ml: 2, flex: 1 }

const ImageButton = ({ item, isSelected, onConfigButton, onSelected }) => {
  const handleConfigButton = useCallback(() => {
    onConfigButton(item)
  }, [item, onConfigButton])

  const handleSelection = useCallback(() => {
    onSelected(item)
  }, [onSelected, item])

  return (
    <StyledImageCard isSelected={isSelected} onClick={handleSelection}>
      <ConfigButton onClick={handleConfigButton}>
        <CreateIcon fontSize='small' />
      </ConfigButton>
      {item.type === 'video' && item.percent == null
        ? (
          <VideoPreview>
            <source src={`${item.url}#t=1`} />
          </VideoPreview>
        ) : (
          <ImagePreview
            variant="square"
            src={item.url}
            alt={item.asset_id}
            sx={imagePreviewStyle}
          />
        )}
      {item.type && <TypeChip>{item.type}</TypeChip>}
      <ProgressBar percent={item.percent} />
    </StyledImageCard>
  )
}

const TagButton = styled.button`
  width: fit-content;
  cursor: pointer;
  border: 4px solid transparent;
  margin-right: 8px;
  border-radius: 8px;
  ${p => p.isSelected && `
    border: 4px solid #2196f3;
  `}
`

const UploadInput = styled.input`
  display: none;
`

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  height: 5px;
  background: #119d83;
  transition: width .5s;
  ${p => p.percent && p.percent !== 100 && `
    width: ${p.percent}%;
  `}
`

const ConfigButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  cursor: pointer;
  border: 0;
  border-radius: 4px;
`

const TypeChip = styled.div`
  position: absolute;
  bottom: 4px;
  left: 4px;
  width: auto;
  z-index: 1;
  color: white;
  background: #56a3e1;
  border-radius: 5px;
  padding: 2px 4px;
  font-size: 10px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const VideoPreview = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const ImagePreview = styled(Avatar)`
  background: transparent;
  img {
    object-fit: contain;
  }
`

const appBarStyle = { position: 'relative' }

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
});

const AssetManager = () => {
  const dispatch = useDispatch()
  const currentSite = useSelector(state => state.site)
  const openAssets = useSelector(state => state.openAssets ?? { isOpen: false, showSelection: false })
  const [assets, setAssets] = useState([])
  const [tags, setTags] = useState([])
  const [types, setTypes] = useState([])
  const [selectedAsset, setSelectedAsset] = useState(null)
  const [configAsset, setConfigAsset] = useState(null)
  const [showLoadingAssets, setShowLoadingAssets] = useState(false)
  const [search, setSearch] = useState([])
  const uploadRef = useRef(null)

  const searchResults = useMemo(() => {
    if (search.length === 0) return assets
    return assets.filter(asset => (asset.tags.filter(tag => search.includes(tag)).length > 0))
  }, [assets, search])

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault()
      const stillUploading = assets.filter(a => a.percent && a.percent < 100)
      if (stillUploading.length > 0) {
        return e.returnValue = "Some assets are still uploading. Are you sure you want to close?";
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [assets])

  const handleCloseAssets = useCallback(() => dispatch({ type: 'open-assets', data: { isOpen: false, showSelection: false } }), [dispatch])

  useEffect(() => {
    const getInitialData = async () => {
      if (currentSite?.site_id) {
        const responses = await Promise.all([
          get(`${ASSETS_URL}?site_id=${currentSite?.site_id}`),
          get(`${TAGS_URL}?site_id=${currentSite?.site_id}`),
          get(`${TYPES_URL}?site_id=${currentSite?.site_id}`)
        ])
        const [assetsResponse, tagsResponse, typesResponse] = responses;
  
        const currentAssets = assetsResponse.map(asset => ({ 
          ...asset, 
          url: asset.type === 'image' ? 
          `${process.env.REACT_APP_ASSETS_URL}/images/${asset.asset_id}?s=256x256&fit=contain` : 
          `${process.env.REACT_APP_ASSETS_URL}/assets/${asset.asset_id}`
        }))
        setAssets(currentAssets)
  
        const currentTags = tagsResponse.map(tag => tag.name)
        setTags(currentTags)
  
        const currentTypes = typesResponse.map(type => type.name)
        setTypes(currentTypes)
      }
    }
    getInitialData();
  }, [currentSite?.site_id])

  const updateTags = useCallback((assetTags) => {
    setTags(currentTags => {
      const newTags = []
      assetTags.forEach(t => {
        if (currentTags.indexOf(t) === -1) {
          newTags.push(t)
        }
      })
      return [...currentTags, ...newTags]
    })
  }, [])

  const updateTypes = useCallback((assetType) => {
    setTypes(currentTypes => {
      if (currentTypes.indexOf(assetType) === -1) {
        return [...currentTypes, assetType]
      }
      return currentTypes
    })
  }, [])

  const getFileType = useCallback(async (url) => {
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);

      xhr.onreadystatechange = () => {
        const contentType = xhr.getResponseHeader('Content-Type');
        if (contentType) {
          xhr.abort();
          resolve(contentType)
        }
      };

      xhr.send();
    });
  }, []);

  const handleSelect = useCallback(async () => {
    let value = ''
    if(typeof selectedAsset === 'string') {
      value = `asset('${selectedAsset}').source`
    } else {
      if (selectedAsset.type === 'image' || selectedAsset.type === 'imagen') {
        value = `/images/${selectedAsset?.asset_id}?t=desktop`
      } else {
        const mineType = await getFileType(selectedAsset?.url)
        if (mineType.includes('image')) {
          value = `/images/${selectedAsset?.asset_id}?t=desktop`
        } else {
          value = `/assets/${selectedAsset?.asset_id}`
        }
      }
    }
    dispatch({ type: 'image-mutator-value', data: { path: openAssets?.path, value } })
    handleCloseAssets()
  }, [dispatch, getFileType, handleCloseAssets, openAssets?.path, selectedAsset])

  const handleUploadOpen = useCallback(() => {
    if (uploadRef && uploadRef.current) {
      const upload = uploadRef.current
      upload.click()
    }
  }, [])

  const handleUploadProgress = useCallback((event, asset_id, type) => {
    const attribute = `${type}Percent`
    const percent = Math.floor((event.loaded / event.total) * 100)
    setAssets((currentAssets) => currentAssets.map(a => {
      if (a.asset_id === asset_id) {
        const otherPercent = type === 'signed' ? a.clientSignedPercent : a.signedPercent
        const totalPercent = Math.round((otherPercent + percent) / 2)
        return {
          ...a,
          [attribute]: percent,
          percent: totalPercent,
        }
      }
      return a
    }))
  }, [])

  const filePutSignedUrl = useCallback(async (url, file, asset_id, type) => {
    return new Promise((resolve) => {
      const request = new XMLHttpRequest();
      request.open('PUT', url);
      request.setRequestHeader('Content-Type', file.type);

      request.upload.addEventListener('progress', (e) => handleUploadProgress(e, asset_id, type));
      request.addEventListener('load', resolve);

      request.send(file);
    });
  }, [handleUploadProgress]);

  const getExtType = useCallback((fileName) => {
    if (isExtImage(fileName)) return 'image'
    if (isExtVideo(fileName)) return 'video'
    return 'file'
  }, [])

  const handleUploadFiles = useCallback((e) => {
    const files = e.target.files

    if (files != null) {
      setShowLoadingAssets(true)
      Promise.all([...files].map(file => new Promise(async (resolve) => {
        const { asset_id, signedUrl, clientSignedUrl } = await post(ASSETS_URL, {
          site_id: currentSite?.site_id,
          mimeType: file.type
        })

        const base64Url = await toBase64(file)
        const currentType = getExtType(file.name)

        setAssets((currentAssets) => [{
          asset_id,
          type: currentType,
          tags: [],
          url: base64Url ?? asset_id,
          signedPercent: 0,
          clientSignedPercent: 0,
          percent: 0, 
        }, ...currentAssets])

        post(CREATE_URL, {
          asset_id,
          tags: [],
          type: currentType,
          site_id: currentSite?.site_id
        })

        updateTypes(currentType)
  
        filePutSignedUrl(signedUrl, file, asset_id, 'signed')
        filePutSignedUrl(clientSignedUrl, file, asset_id, 'clientSigned')
        resolve()
      })))
    } else {
      alert('Por favor, selecciona un archivo.')
    }
  }, [currentSite?.site_id, filePutSignedUrl, getExtType, updateTypes])

  const handleConfigClose = useCallback((_, reason) => {
    if (reason !== 'backdropClick') {
      setConfigAsset(null)
    }
  }, [])

  const handleAssetChange = useCallback((assetTags, assetType, assetMeta, assetId) => {
    updateTags(assetTags)
    updateTypes(assetType)
    setAssets(currentAssets => currentAssets.map(a => {
      if (a.asset_id === assetId) {
        return {
          ...a,
          tags: assetTags,
          type: assetType,
          ...(assetMeta && { metadata: assetMeta }),
        }
      }
      return a
    }))

    put(ASSETS_URL, {
      asset_id: assetId,
      tags: assetTags,
      type: assetType,
      ...(assetMeta && { metadata: assetMeta }),
    })
  }, [updateTags, updateTypes])

  const hideLoadingAssets = useCallback(() => {
    setShowLoadingAssets(false)
    setAssets(currentAssets => currentAssets.map(a => {
      if (a.percent === 100) {
        return {
          ...a,
          percent: undefined,
        }
      }
      return a
    }))
  }, [])

  const handleAssetTagsChange = useCallback((asset_id, currentTags) => {
    setAssets(currentAssets => currentAssets.map(a => {
      if (a.asset_id === asset_id) {
        return {
          ...a,
          tags: currentTags,
        }
      }
      return a
    }))
  }, [])

  const handleAssetTypeChange = useCallback((asset_id, currentType) => {
    setAssets(currentAssets => currentAssets.map(a => {
      if (a.asset_id === asset_id) {
        return {
          ...a,
          type: currentType,
        }
      }
      return a
    }))
  }, [])

  return (
    <>
      <Dialog
        fullScreen
        open={openAssets?.isOpen}
        onClose={handleCloseAssets}
        TransitionComponent={Transition}
      >
        <AppBar sx={appBarStyle}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleCloseAssets}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography sx={titleStyle} variant="h6" component="div">
              Assets Collector
            </Typography>
            <Button autoFocus color="inherit" onClick={handleUploadOpen}>
              Upload
            </Button>
            <UploadInput type='file' ref={uploadRef} onChange={handleUploadFiles} multiple />
          </Toolbar>
        </AppBar>
        <BodyContainer>
          <AutocompleteChip placeholder='Search...' options={tags} value={search} onChange={setSearch} disableNewOption />
          {openAssets?.showSelection && (
            <TagsContainer>
              {tags.map((tag) => (
                <TagButton
                  isSelected={tag === selectedAsset}
                  onClick={() => setSelectedAsset(tag)}
                  key={tag}
                >
                  {tag}
                </TagButton>
              ))}
            </TagsContainer>
          )}
          <ImageList>
            {searchResults.map((item) => (
              <ImageButton
                key={item.url}
                onConfigButton={setConfigAsset}
                onSelected={setSelectedAsset}
                item={item}
                isSelected={item === selectedAsset}
              >
                
              </ImageButton>
            ))}
          </ImageList>
        </BodyContainer>
        {openAssets?.showSelection && (
          <DialogActions>
            <Button onClick={handleSelect}>Select</Button>
          </DialogActions>
        )}
      </Dialog>
      <AssetConfig
        open={configAsset !== null}
        onClose={handleConfigClose}
        asset={configAsset}
        onSave={handleAssetChange}
        tags={tags}
        types={types}
      />
      {showLoadingAssets && (
        <LoadingAssets
          assets={assets}
          tags={tags}
          types={types}
          onClose={hideLoadingAssets}
          onSave={handleAssetChange}
          onConfigOpen={setConfigAsset}
          onTagsChange={handleAssetTagsChange}
          onTypeChange={handleAssetTypeChange}
        />
      )}
    </>
  )
}

export default AssetManager